import { Stack } from "@mui/material";
import { BackButton, NextButton } from "./styled-components/Buttons";
import {
  resetAddress,
  resetEntityAddress,
  setApplicationStep,
  setEntityAddress,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import { useAppDispatch } from "src/store/store";
import { FormikValues, useFormikContext } from "formik";
import { trackEvent } from "src/Amplitude";
import {
  EntityDetailsDto,
  DataZooQueuePayload,
  useProcessOnboardingQueuesMutation,
} from "src/store/features/api/generated";

export default function ApplicationNavigation() {
  const {
    accountType,
    address: {
      country: addrCountry,
      state: addrState,
      street: addrStreet,
      zipCode,
      city: addrCity,
    },
    phone: phoneNum,
    birthdate: { day, month, year },
    applicationStep,
    uploadedFiles,
    address,
    ssn,
    doesNotHoldSSN,
    ignoreFileUpload,
    entityDetails: { recordType, companyType },
  } = useOnboarding();

  const [processOnboardingQueues, { isLoading }] = useProcessOnboardingQueuesMutation();

  const formik = useFormikContext<FormikValues>();
  const { validateForm, submitForm, setErrors, errors } = formik;
  const dispatch = useAppDispatch();

  const handleBackButton = () => {
    switch (applicationStep) {
      case "aboutYou":
        if (accountType === "Entity") {
          dispatch(resetEntityAddress());
          dispatch(resetAddress());
          dispatch(setApplicationStep("aboutYourEntity"));
        } else {
          dispatch(setApplicationStep("accountType"));
        }
        break;
      case "aboutYourEntity":
        dispatch(setApplicationStep("nonIndividualTypeSelection"));
        break;
      case "cryptoJourney":
        dispatch(setApplicationStep("aboutYou"));
        break;
      default:
        break;
    }
  };

  const handleEntityNextButton = async () => {
    // Industry and occupation validation
    await validateIndustryAndOccupation();

    // Validate form
    const {
      country,
      state,
      postCode,
      streetAddress,
      unitApartment,
      entityType,
      entityName,
      entityId,
    } = await validateForm();

    const containsError =
      country ||
      state ||
      postCode ||
      streetAddress ||
      unitApartment ||
      entityType ||
      entityName ||
      entityId;

    if (containsError) return; // Stop user from procceeding if there are errors

    // Push address to entity Address
    if (accountType === "Entity")
      dispatch(setEntityAddress(address as EntityDetailsDto["address"]));

    // // Send uploaded documents to non individual documents
    // dispatch(setEntityDocuments(uploadedFiles));

    // Check if there is at least 1 file uploaded
    const minimunFileRequired = uploadedFiles.length > 0;

    // check if any of the files are loading
    const anyOfTheFilesIsLoading = uploadedFiles.some((file) => file.isLoading);

    // check if any of the files contain errors
    const anyOfTheFilesContainsErrors = uploadedFiles.some((file) => file.isError);

    if (recordType === "Other" && !companyType)
      return window.alert("Please select your company type");

    if (!minimunFileRequired) return window.alert("Please upload at least one Document");
    if (anyOfTheFilesContainsErrors) return window.alert("One or more files are invalid");
    if (anyOfTheFilesIsLoading) return window.alert("Please wait until the files are uploaded");

    // Reset non entity address
    dispatch(resetAddress());

    dispatch(setApplicationStep("aboutYou"));
  };

  const validateIndustryAndOccupation = async () => {
    const { industry, occupation } = errors;
    if (industry || occupation) {
      // Adding a custom error handling since this is validateForm triggers Yup validation
      return;
    }
  };

  const handleIndividualNextButton = async () => {
    // Industry and occupation validation
    await validateIndustryAndOccupation();

    // Trigger validation before proceeding to the next phase
    const {
      phone,
      country,
      state,
      postCode,
      streetAddress,
      unitApartment,
      birthDay,
      birthMonth,
      birthYear,
      ssn: ssnError,
      nationality,
    } = await validateForm();

    // Check if there is at least 1 file uploaded
    const minimunFileRequired = uploadedFiles.length > 0;

    // check if any of the files are loading
    const anyOfTheFilesIsLoading = uploadedFiles.some((file) => file.isLoading);

    // check if any of the files are loading
    const anyOfTheFilesContainsErrors = uploadedFiles.some((file) => file.isError);

    // Contain errors if any are true
    const containErrors =
      phone ||
      nationality ||
      country ||
      state ||
      postCode ||
      streetAddress ||
      unitApartment ||
      birthDay ||
      birthMonth ||
      birthYear ||
      ssnError ||
      anyOfTheFilesContainsErrors ||
      anyOfTheFilesIsLoading;

    if (nationality === "American" || address.country === "United States") {
      if (!doesNotHoldSSN && !ssn) return setErrors({ ssn: "SSN is required" });
    }

    if (applicationStep === "aboutYou") {
      // Don't alert if user can bypass file upload
      if (!minimunFileRequired && !ignoreFileUpload) {
        return window.alert("Please upload at least one proof of address");
      }
      if (anyOfTheFilesContainsErrors) window.alert("One or more files are invalid");
      if (anyOfTheFilesIsLoading) window.alert("Please wait until the files are uploaded");

      if (!containErrors) {
        // send data to datazoo for processing

        // if country is United States, replace with United States of America (USA)
        const countryToSubmit =
          addrCountry === "United States" ? "United States of America (USA)" : addrCountry;

        const dataZooQueuePayload: DataZooQueuePayload = {
          address: {
            city: addrCity || "",
            street: addrStreet || "",
            country: countryToSubmit || "",
            state: addrState || "",
            zipCode: zipCode || "",
          },
          phoneNumber: phoneNum.replace(/\s+/g, "") ?? "",
          dateOfBirth: `${year}-${month}-${day}` || "",
          ssn: ssn ?? "",
        };

        await processOnboardingQueues({
          dataZooQueuePayload,
        });

        // then set application step to crypto journey
        dispatch(setApplicationStep("cryptoJourney"));
      }

      // Amplitude tracking individual application form
      trackEvent("Guest initiated individual application form", { applicationStep }, "Onboarding");
    }

    if (applicationStep === "cryptoJourney") {
      if (!containErrors) submitForm();
    }

    if (accountType === "Entity") {
      // Next was pressed and account type is Entity, track event
      trackEvent("Guest initiated Entity application form", { accountType }, "Onboarding");
    }
  };

  return (
    <Stack direction="row" spacing="2" justifyContent="space-between" mt={2} mb={2}>
      <BackButton variant="outlined" onClick={handleBackButton}>
        <span translate="no">Back</span>
      </BackButton>
      <NextButton
        type="button"
        onClick={
          applicationStep === "aboutYourEntity"
            ? handleEntityNextButton
            : handleIndividualNextButton
        }
        disabled={isLoading}>
        {applicationStep === "aboutYourEntity" && <span translate="no">Next Step</span>}
        {applicationStep === "aboutYou" && <span translate="no">Next Step</span>}
        {applicationStep === "cryptoJourney" && <span translate="no">Submit Application</span>}
      </NextButton>
    </Stack>
  );
}
